import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import CardBox from '../../CardBox';
import { colors, fontSize } from '../../../constants/styleConstants';
import RoleSwitch from './RoleSwitch';
import TextButton from '../../TextButton';
import { PRIVACY_ATTRIBUTE_IDS, TP } from '../../../constants/consts';
import { updateOne } from '../../../containers/App/actions';
import { PROFILE_REPERTOIRE } from '../../../constants/endpointConstants';
import { updateRepertoireAction } from '../../../containers/Artist/actions';

const useStyles = makeStyles(theme => ({
  main: {
    marginBottom: 12,
    padding: 12,
    boxSizing: 'border-box',
    alignItems: 'flex-start',
    fontSize: fontSize.medium,
  },
  work: {
    display: 'flex',
    alignItems: 'baseline',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  workName: {
    width: '30%',
    maxWidth: '340px',
    fontWeight: 'bold',
    color: colors.lightTitle,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 15,
      width: '100%',
    },
  },
  composer: {
    padding: '10px 0',
    fontWeight: 'bold',
  },
  roles: {
    flexGrow: 1,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0 8px',
    marginBottom: 8,
  },
  hidden: {
    backgroundColor: colors.background.dark,
  },
  [theme.breakpoints.down('xs')]: {
    buttonLabel: {
      fontSize: 0,
    },
    hideButton: {
      minWidth: 'auto',
    },
  },
}));

const RepertoireCard = ({ repertoireEntry, onStatusChange, onDelete }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { works, composer } = repertoireEntry;

  const switchVisibility = (role, privacyId) => {
    dispatch(
      updateOne({
        endpoint: PROFILE_REPERTOIRE(role.profile?.id),
        sagaRoutine: updateRepertoireAction,
        id: role.id,
        params: { privacyAttribute: { id: privacyId } },
      }),
    );
  };

  return (
    <CardBox className={classes.main}>
      <div className={classes.composer}>{get(composer, 'name')}</div>
      {works.map(work => (
        <div key={get(work, 'id')} className={classes.work}>
          <div className={classes.workName}>{get(work, 'name')}</div>
          <div className={classes.roles}>
            {work.roles.map(role => (
              <div
                className={cn(classes.controls, {
                  [classes.hidden]: role.privacyAttribute?.id === PRIVACY_ATTRIBUTE_IDS.PRIVATE,
                })}
              >
                <RoleSwitch
                  key={get(role, 'repertoireId')}
                  role={role}
                  onStatusChange={onStatusChange}
                  onDelete={onDelete}
                />
                {role.privacyAttribute?.id === PRIVACY_ATTRIBUTE_IDS.PRIVATE ? (
                  <TextButton
                    startIcon={<VisibilityOffIcon />}
                    onClick={() => switchVisibility(role, PRIVACY_ATTRIBUTE_IDS.PUBLIC)}
                    classes={{ label: classes.buttonLabel }}
                    className={classes.hideButton}
                  >
                    {t(`${TP}.FN_UNHIDE`)}
                  </TextButton>
                ) : (
                  <TextButton
                    startIcon={<VisibilityIcon />}
                    onClick={() => switchVisibility(role, PRIVACY_ATTRIBUTE_IDS.PRIVATE)}
                    classes={{ label: classes.buttonLabel }}
                    className={classes.hideButton}
                  >
                    {t(`${TP}.FN_HIDE`)}
                  </TextButton>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </CardBox>
  );
};

RepertoireCard.propTypes = {
  onStatusChange: PropTypes.func.isRequired,
  repertoireEntry: PropTypes.object.isRequired,
};

export default RepertoireCard;
